import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import {
  ADDRESS_SUGGESTION_WEIGHT_LIMIT,
  extractConfirmationLevel,
  getAddressConfirmationWeight,
  validationResult2Address
} from "./addressValidation.constant";
import { ShippingAddressData } from "src/state/ShippingAddress/ShippingAddressData";
import { useBloc } from "@blac/react";
import { ShippingAddressValidationCubit } from "src/state/ShippingAddress/ShippingAddressValidationCubit";
import { Validation2AddressSuggestion } from "src/state/ShippingAddress/Validation2AddressSuggestion";

interface ProcessedValidationResult {
  offerSuggestion: boolean;
  confirmationWeight: number;
  suggestion: Validation2AddressSuggestion;
}

export const useValidateShippingAddressWithApi = () => {
  const [, { validateAddressDirect }] = useBloc(ShippingAddressValidationCubit);

  return {
    /**
     * Calls an address validating api and provides assessment on the provided address.
     * Any suggestion made by the api will be included in the result.
     * See: [ValidationResult - response from validation](https://developers.google.com/maps/documentation/address-validation/reference/rest/v1/TopLevel/validateAddress#ValidationResult)
     */
    validateShippingAddressWithApi: async (data: ShippingAddressData) => {
      BlockingLoadingOverlayController.startLoading({
        bg: "transparent",
        fadeIn: true,
        message: "shippingAddress.verification"
      });

      return new Promise<ProcessedValidationResult>((resolve, reject) => {
        // Next step: Full address validation by address api (Google)
        void validateAddressDirect(data)
          .then((result) => {
            if (!result) {
              BlockingLoadingOverlayController.loadingError();
              reject(new Error("Address validation failed"));
              return;
            }
            const confirmationWeight = getAddressConfirmationWeight(
              extractConfirmationLevel(result)
            );
            const suggestion = validationResult2Address({
              result,
              fallback: data
            });
            const offerSuggestion =
              suggestion.differsFromFallback ||
              confirmationWeight > ADDRESS_SUGGESTION_WEIGHT_LIMIT;

            if (offerSuggestion) {
              BlockingLoadingOverlayController.endLoading();
            } else {
              BlockingLoadingOverlayController.loadingSuccess({
                message: "shippingAddress.verified"
              });
            }
            resolve({
              offerSuggestion,
              confirmationWeight,
              suggestion
            });
            return;
          })
          .catch(reject);
      });
    }
  };
};
